import { ColorTokenKey } from '../../indigofair/data/ColorTokenKey';
import { ContentWidth } from '../../indigofair/data/ContentWidth';
import { IAction } from '../../indigofair/data/IAction';
import { IAlertBannerComponent } from '../../indigofair/data/IAlertBannerComponent';
import { IBannerComponent } from '../../indigofair/data/IBannerComponent';
import { IBrandInformationComponent } from '../../indigofair/data/IBrandInformationComponent';
import { IBrandReviewBannerComponent } from '../../indigofair/data/IBrandReviewBannerComponent';
import { IButtonComponent } from '../../indigofair/data/IButtonComponent';
import { ICarouselComponent } from '../../indigofair/data/ICarouselComponent';
import { IClientDrivenComponent } from '../../indigofair/data/IClientDrivenComponent';
import { ICollapsibleListWebBannerComponent } from '../../indigofair/data/ICollapsibleListWebBannerComponent';
import { IComponentLoggingData } from '../../indigofair/data/IComponentLoggingData';
import { IDualContentBannerComponent } from '../../indigofair/data/IDualContentBannerComponent';
import { IFilterableCarouselComponent } from '../../indigofair/data/IFilterableCarouselComponent';
import { IFilterBarComponent } from '../../indigofair/data/IFilterBarComponent';
import { IFlowRowComponent } from '../../indigofair/data/IFlowRowComponent';
import { IGridComponent } from '../../indigofair/data/IGridComponent';
import { IHeaderComponent } from '../../indigofair/data/IHeaderComponent';
import { IImageCollageComponent } from '../../indigofair/data/IImageCollageComponent';
import { ILayoutElement } from '../../indigofair/data/ILayoutElement';
import { IResponsiveSpacing } from '../../indigofair/data/IResponsiveSpacing';
import { ISiteBannerComponent } from '../../indigofair/data/ISiteBannerComponent';
import { ISpacerComponent } from '../../indigofair/data/ISpacerComponent';
import { ITileWrapperComponent } from '../../indigofair/data/ITileWrapperComponent';
import { Spacing } from '../../indigofair/data/Spacing';
import { WebClientSize } from '../../indigofair/data/WebClientSize';

export namespace IComponent {
  export enum LoadingState {
    LOADING_STATE_UNKNOWN = 'LOADING_STATE_UNKNOWN',
    LOADED = 'LOADED',
    PENDING = 'PENDING',
  }

  export namespace IComponentStyle {
    /**
     * Return an instance of IComponentStyle with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IComponentStyle>
    ): IComponentStyle =>
      ({
        ...(partial ?? {}),
      }) as IComponentStyle;
  }

  /**
   * The tags are out of order. All tag numbers must be unique. When adding fields, also update the field below and the tag number.
   * See https:www.notion.so/faire/Protocol-Buffers-Protos-ba527d2f70c34ab983b8d0417c6a56ac#6a792ceb9d37498795a117e712b9e7e7 for details.
   * The last tag added was `brandReviewBannerComponent`. The next tag number is 27.
   */
  export interface IComponentStyle {
    /**
     * Background color of the display area of a component
     */
    background_color: keyof typeof ColorTokenKey | undefined;
    /**
     * The spacing between the top of the display area and the content area of the component.
     * This property acts as a fallback value for responsiveTopSpacing, and will be used if the responsiveTopSpacing
     * value does not target certain viewports.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    top_spacing: keyof typeof Spacing | undefined;
    /**
     * The spacing between the top of the full display area and the content area of the component,
     * defined on a per viewport basis. This property will take precedence over the topSpacing if provided.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    responsive_top_spacing: IResponsiveSpacing | undefined;
    /**
     * The spacing between the bottom of the display area and the content area of the component.
     * This property acts as a fallback value for responsiveBottomSpacing, and will be used if the
     * responsiveBottomSpacing value does not target certain viewports.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    bottom_spacing: keyof typeof Spacing | undefined;
    /**
     * The spacing between the bottom of the full display area and the content area of the component,
     * defined on a per viewport basis. This property will take precedence over the bottomSpacing if provided.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    responsive_bottom_spacing: IResponsiveSpacing | undefined;
    /**
     * The width of the content area that a component will render in.
     * This is akin to the max width of the content.
     * DEPRECATED: Use `contentWidth` at the Section level instead.
     * @deprecated
     */
    content_width: keyof typeof ContentWidth | undefined;
    /**
     * The spacing between the left and right edge of the content width and context area of the component.
     * This property acts as a fallback value for responsiveHorizontalSpacing, and will be used if the
     * responsiveHorizontalSpacing value does not target certain viewports.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    horizontal_spacing: keyof typeof Spacing | undefined;
    /**
     * The spacing between the left and right edge of the content width and context area of the component,
     * defined on a per viewport basis. This property will take precedence over the horizontalSpacing if provided.
     * Please see the SDUI Viewport Customization, Customizing components via Responsive Properties section
     * to better understand the responsive property behaviour.
     * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4cbc7cd891604f06b91405173f1988e1
     */
    responsive_horizontal_spacing: IResponsiveSpacing | undefined;
  }

  /**
   * Return an instance of IComponent with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IComponent>): IComponent =>
    ({
      on_view: [],
      viewports: [],
      ...(partial ?? {}),
    }) as IComponent;
}

export interface IComponent {
  id: string | undefined;
  logging_data: IComponentLoggingData | undefined;
  on_view: Array<IAction>;
  /**
   * Message that describe the component level styling of an SDUI Component.
   * Please see the SD UI Component Content Model Documentation for a more visual explanation of the properties
   * https:www.notion.so/faire/SD-UI-Component-Content-Model-8797f496a60048269e3a7d8dde83d300
   */
  style: IComponent.IComponentStyle | undefined;
  /**
   * Array of viewports that the component should be rendered in. By default, an empty array which does not limit the
   * component to any viewports.
   * Please see the SDUI Viewport Customization, Displaying different components per viewport for more information
   * https:www.notion.so/faire/SDUI-Viewport-Customization-1e40d4862e8443cfbb36136150373cc7?pvs=4#4851c28618ed4b93908897495a550d34
   */
  viewports: Array<keyof typeof WebClientSize>;
  /**
   * A flag to indicate if this component should be included in UPLT/TTL measurement
   */
  is_required_for_interactivity: boolean | undefined;
  loading_state: keyof typeof IComponent.LoadingState | undefined;
  layout_element: ILayoutElement | undefined;
  carousel_component: ICarouselComponent | undefined;
  banner_component: IBannerComponent | undefined;
  header_component: IHeaderComponent | undefined;
  spacer_component: ISpacerComponent | undefined;
  alert_banner_component: IAlertBannerComponent | undefined;
  grid_component: IGridComponent | undefined;
  /**
   * @deprecated
   */
  filterable_carousel_component: IFilterableCarouselComponent | undefined;
  filter_bar_component: IFilterBarComponent | undefined;
  flow_row_component: IFlowRowComponent | undefined;
  collapsible_list_web_banner_component:
    | ICollapsibleListWebBannerComponent
    | undefined;
  button_component: IButtonComponent | undefined;
  dual_content_banner_component: IDualContentBannerComponent | undefined;
  client_driven_component: IClientDrivenComponent | undefined;
  site_banner_component: ISiteBannerComponent | undefined;
  image_collage_component: IImageCollageComponent | undefined;
  tile_wrapper_component: ITileWrapperComponent | undefined;
  brand_information_component: IBrandInformationComponent | undefined;
  brand_review_banner_component: IBrandReviewBannerComponent | undefined;
}
